import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type User, default as api } from '../service/api';
import { createPersistStorage } from './utils/persist';

interface UserState {
  user: User | null;
  balance: number;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean; // Track if initial session check is done
  pendingInvitationCode: string | null;
}

interface UserActions {
  setUser: (user: User) => void;
  clearUser: () => void;
  updateBalance: () => Promise<void>;
  checkSession: () => Promise<void>;
  setPendingInvitationCode: (code: string | null) => void;
  clearPendingInvitationCode: () => void;
}

type UserStore = UserState & UserActions;

type UserStorePersist = Pick<
  UserStore,
  'user' | 'balance' | 'pendingInvitationCode'
>;

const useUserStore = create<UserStore>()(
  persist<UserStore, [], [], UserStorePersist>(
    (set, get) => ({
      user: null,
      balance: 0,
      isLoading: false,
      error: null,
      isInitialized: false,
      pendingInvitationCode: null,

      setUser: user => {
        set({ user });
        get().updateBalance();
      },

      clearUser: () =>
        set({
          user: null,
          balance: 0,
          isLoading: false,
          isInitialized: false,
          pendingInvitationCode: null,
          error: null,
        }),

      updateBalance: async () => {
        set({ isLoading: true, error: null });
        try {
          const balance = await api.transactionsGetCurrentUserBalance();
          set({ balance, isLoading: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to load balance';
          set({ error: errorMessage, isLoading: false });
          console.error('Failed to update balance:', error);
        }
      },

      checkSession: async () => {
        // Don't set isInitialized until session check completes
        // to prevent child components from making API calls with an expired session
        set({ isLoading: true });

        try {
          const hasSession = await api.sessionSync();
          if (!hasSession) {
            // Session is invalid — clear cached user to prevent redirect loops
            set({
              user: null,
              balance: 0,
              isInitialized: true,
              isLoading: false,
            });
            return;
          }

          const user = await api.userCurrentGet();
          set({ user, isInitialized: true, isLoading: false });
          get().updateBalance();
        } catch (error) {
          console.error('Failed to fetch current user:', error);
          // Clear cached user on error to prevent redirect loops
          set({
            user: null,
            balance: 0,
            isInitialized: true,
            isLoading: false,
          });
        }
      },

      setPendingInvitationCode: (code: string | null) =>
        set({ pendingInvitationCode: code }),
      clearPendingInvitationCode: () => set({ pendingInvitationCode: null }),
    }),
    {
      name: 'user-store',
      storage: createPersistStorage<UserStorePersist>(),
      partialize: state => ({
        user: state.user,
        balance: state.balance,
        pendingInvitationCode: state.pendingInvitationCode,
      }),
    },
  ),
);

export default useUserStore;
